import Vue from 'vue';
import Router from 'vue-router';
import lazyLoading from './lazy-loading';
import Api from '@/services/Api';
import store from '@/store';
import role from '@/shared/constants/role';
import {
  nextFactory,
  authMiddleware as auth,
  guestMiddleware as guest,
  rolesMiddleWare as roles,
  validityMiddleware as validity,
  planMiddleware as plan,
  planOptionMiddleware as planOption,
  parseJwtMiddleware as parseJwt,
  regionMiddleware as region,
  accessLanguage,
  planUserGenerateMiddleware,
  canUseGenerateConceptMiddleware as generateConcept,
  canUseValuationShelveMiddleware as valuationShelve,
  canUseValuationSurveyMiddleware as valuationSurvey,
  canUseConceptValuationMiddleware as conceptValuation,
  canUseAiGroupInterviewMiddleware as aiGroupInterview,
  generateFreePlanCreatedMiddleware as generateFreePlanCreated,
  homeByRegion,
  preventUserTrialWithoutData,
} from './middleware';
import BackendLayout from '@/layout/BackendLayout';
import { addServiceFacebook, removeRecaptcha, saveRouteAfter } from '../shared/functions';
import constants from "../shared/constants/constants";

Vue.use(Router);

const router = new Router({
  mode: 'history',
  routes: [
    {
      path: '',
      name: 'HomePage',
      component: lazyLoading('layout/HomePage'),
      meta: {
        middleware: [homeByRegion],
        title: 'PLUG AI中国版',
      },
    },
    {
      path: '/',
      redirect: { name: 'Login' },
      component: lazyLoading('layout/UserLayout'),
      children: [
        {
          path: '/register/trial_register',
          name: 'TrialRegister',
          component: lazyLoading('components/customer/register/TrialRegister'),
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.NEW_USER_REGISTRATION,
          }
        },
        {
          path: '/register/trial_register_generate',
          name: 'TrialRegisterGenerate',
          component: lazyLoading('components/customer/register/TrialRegister'),
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.NEW_USER_REGISTRATION,
          }
        },
        {
          path: '/register/trial_register_valuation',
          name: 'TrialRegisterValuation',
          component: lazyLoading('components/customer/register/TrialRegister'),
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.NEW_USER_REGISTRATION,
          }
        },
        {
          path: '/register/pre_register',
          name: 'PreRegister',
          component: lazyLoading('components/customer/register/PreRegister'),
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.ENTER_EMAIL,
          }
        },
        {
          path: '/register/mail_send',
          name: 'MailSend',
          component: lazyLoading('components/customer/register/MailSend'),
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.SEND_COMPLETE,
          },
        },
        {
          path: '/register/plan_select',
          name: 'PlanSelect',
          component: lazyLoading('components/customer/register/PlanSelect'),
          beforeEnter(to, from, next) {
            if (to.params.user) {
              next();
            } else {
              next({name: 'Login'});
            }
          },
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.PLAN_ELECTION,
          },
        },
        {
          path: '/register/confirm',
          name: 'RegisterConfirm',
          component: lazyLoading('components/customer/register/RegisterConfirm'),
          beforeEnter(to, from, next) {
            if (to.params.user) {
              next();
            } else {
              next({name: 'Login'});
            }
          },
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.CONFIRM_CONTENT,
          },
        },
        {
          path: '/register/complete',
          name: 'RegisterComplete',
          component: lazyLoading('components/customer/register/RegisterComplete'),
          beforeEnter(to, from, next) {
            if (to.params.user) {
              next();
            } else {
              next({name: 'Login'});
            }
          },
          meta: {
            middleware: [parseJwt, guest, region],
            title: constants.TITLE_LANG.REGISTRATION_COMPLETED,
          },
        },
        {
          path: '/register/plan_renew',
          name: 'PlanRenew',
          component: lazyLoading('components/customer/register/PlanRenew'),
          meta: {
            middleware: [parseJwt, auth, preventUserTrialWithoutData],
            title: constants.TITLE_LANG.PLAN_UPDATE,
          },
        },
        {
          path: '/register/update_information',
          name: 'UpdateInfoUserTrial',
          component: lazyLoading('components/customer/register/UpdateInfoUserTrial'),
          meta: {
            middleware: [parseJwt, auth],
            title: constants.TITLE_LANG.PLAN_UPDATE,
          },
        },
        {
          path: '/register/plan_confirm',
          name: 'PlanConfirm',
          component: lazyLoading('components/customer/register/PlanConfirm'),
          meta: {
            middleware: [parseJwt, auth, preventUserTrialWithoutData],
            title: constants.TITLE_LANG.CONFIRMATION_UPDATES,
          },
          beforeEnter(to, from, next) {
            if (store.state.plans) {
              next();
            } else {
              next({name: 'PlanRenew'});
            }
          },
        },
        {
          path: '/register/plan_complete',
          name: 'PlanComplete',
          component: lazyLoading('components/customer/register/PlanComplete'),
          meta: {
            middleware: [parseJwt, auth],
            title: constants.TITLE_LANG.UPDATE_COMPLETED,
          },
        },
        {
          path: '/register/password_reset',
          name: 'PasswordReset',
          component: lazyLoading('components/customer/register/PasswordReset'),
          meta: {
            middleware: [parseJwt],
            title: constants.TITLE_LANG.REISSUE_PASSWORD,
          },
        },
        {
          path: '/register/password_reset_send',
          name: 'PasswordResetSend',
          component: lazyLoading('components/customer/register/PasswordResetSend'),
          meta: {
            middleware: [parseJwt],
            title: constants.TITLE_LANG.SEND_COMPLETED,
          },
        },
        {
          path: '/register/password_new/:token',
          name: 'PasswordNew',
          component: lazyLoading('components/customer/register/PasswordNew'),
          meta: {
            middleware: [parseJwt],
            title: constants.TITLE_LANG.PASSWORD_RESET,
          },
        },
        {
          path: '/register/password_complete',
          name: 'PasswordComplete',
          component: lazyLoading('components/customer/register/PasswordComplete'),
          meta: {
            middleware: [parseJwt],
            title: constants.TITLE_LANG.PASSWORD_CHANGE_COMPLETED,
          },
        },
        {
          path: '/register/:token',
          name: 'RegisterIndex',
          component: lazyLoading('components/customer/register/RegisterIndex'),
          meta: {
            middleware: [parseJwt, guest],
            title: constants.TITLE_LANG.NEW_USER_REGISTRATION,
          },
        },
        {
          path: '/material-request',
          name: 'MaterialRequestForm',
          component: lazyLoading('components/customer/material_request/MaterialRequestForm'),
          meta: {
            middleware: [parseJwt],
            title: constants.TITLE_LANG.MATERIAL_REQUEST,
          },
        },
        {
          path: '/material-request/complete',
          name: 'MaterialRequestComplete',
          component: lazyLoading('components/customer/material_request/MaterialRequestComplete'),
          meta: {
            middleware: [parseJwt],
            title: constants.TITLE_LANG.MATERIAL_REQUEST_COMPLETE,
          },
        },
        {
          path: '/concept-valuation',
          name: 'ConceptValuationSelectType',
          component: lazyLoading('components/customer/concept_valuation/SelectType'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.REGISTER,
          },
        },
        {
          path: '/concept-valuation/old-projects',
          name: 'ConceptValuationOldProjectList',
          component: lazyLoading('components/customer/concept_valuation/OldProjectList'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.PREVIOUS_PROJECTS,
          },
        },
        {
          path: '/concept-valuation/new-project',
          name: 'ConceptValuationNewProject',
          component: lazyLoading('components/customer/concept_valuation/NewProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.NEW_PROJECT_REGISTRATION,
          },
        },
        {
          path: '/concept-valuation/menu',
          name: 'ConceptValuationSelectMenu',
          component: lazyLoading('components/customer/concept_valuation/SelectMenu'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.MENU_SELECTION,
          },
          props: true,
        },
        {
          path: '/concept-valuation/segment',
          name: 'ConceptValuationSelectSegment',
          component: lazyLoading('components/customer/concept_valuation/SelectSegment'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.SEGMENT_SETTING,
          },
          props: true,
        },
        {
          path: '/concept-valuation/concepts',
          name: 'ConceptValuationInputConcepts',
          component: lazyLoading('components/customer/concept_valuation/InputConcepts'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.INPUT_CONCEPT,
          },
          props: true,
        },
        {
          path: '/concept-valuation/category',
          name: 'ConceptValuationSelectCategory',
          component: lazyLoading('components/customer/concept_valuation/SelectCategory'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.SELECT_CATEGORY,
          },
          props: true,
        },
        {
          path: '/concept-valuation/confirm',
          name: 'ConceptValuationConfirm',
          component: lazyLoading('components/customer/concept_valuation/Confirm'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.CONTENT_CONFIRMATION,
          },
          props: true,
        },
        {
          path: '/concept-valuation/results',
          name: 'ConceptValuationResult',
          component: lazyLoading('components/customer/concept_valuation/Result'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.VIEW_RESULTS,
          },
          props: true,
        },
        {
          path: '/concept-valuation/analysis-report',
          name: 'ConceptValuationAnalysisResult',
          component: lazyLoading('components/customer/concept_valuation/AnalysisResult'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, conceptValuation],
            title: constants.TITLE_LANG.ANALYSIS_RESULTS,
          },
          props: true,
        },
        {
          path: '/ai-group-interview',
          name: 'AiGiSelectTypeProject',
          component: lazyLoading('components/customer/ai_group_interview/SelectTypeProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.REGISTER,
          },
        },
        {
          path: '/ai-group-interview/old-projects',
          name: 'AiGiSelectOldProject',
          component: lazyLoading('components/customer/ai_group_interview/SelectOldProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.PREVIOUS_PROJECTS,
          },
        },
        {
          path: '/ai-group-interview/new-project',
          name: 'AiGiNewProject',
          component: lazyLoading('components/customer/ai_group_interview/NewProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.NEW_PROJECT_REGISTRATION,
          },
        },
        {
          path: '/ai-group-interview/research-register',
          name: 'AiGiResearchRegister',
          component: lazyLoading('components/customer/ai_group_interview/ResearchRegister'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.AI_GI_RESEARCH_REGISTER,
          },
        },
        {
          path: '/ai-group-interview/confirm',
          name: 'AiGiConfirm',
          component: lazyLoading('components/customer/ai_group_interview/Confirm'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.CONTENT_CONFIRMATION,
          },
        },
        {
          path: '/ai-group-interview/research-request-received',
          name: 'AiGiResearchRequestReceived',
          component: lazyLoading('components/customer/ai_group_interview/ResearchRequestReceived'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.AI_GI_RESEARCH_REQUEST_RECEIVED,
          },
        },
        {
          path: '/ai-group-interview/ai-marketer',
          name: 'AiGroupInterviewResearchAvatar',
          component: lazyLoading('components/customer/ai_group_interview/ResearchIssueAvatarView'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.AI_GI_AI_MARKETER,
          },
        },
        {
          path: '/ai-group-interview/results',
          name: 'AiGiResearchResult',
          component: lazyLoading('components/customer/ai_group_interview/ResearchResult'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, region, aiGroupInterview],
            title: constants.TITLE_LANG.AI_GI_RESEARCH_RESULT,
          },
        },
        {
          path: '/type-project',
          name: 'SelectTypeProject',
          component: lazyLoading('components/customer/survey/SelectTypeProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.REGISTER,
          },
          props: true,
        },
        {
          path: '/project/old-project-list',
          name: 'SelectOldProject',
          component: lazyLoading('components/customer/survey/SelectOldProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.PREVIOUS_PROJECTS,
          },
        },
        {
          path: '/project-create',
          name: 'SurveyIndex',
          component: lazyLoading('components/customer/survey/SurveyIndex'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.NEW_PROJECT_REGISTRATION,
          },
          props: true,
        },
        {
          path: '/project-create/select_segment',
          name: 'SelectSegment',
          component: lazyLoading('components/customer/survey/SelectSegment'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.SEGMENT_SETTING,
          },
          props: true,
        },
        {
          path: '/project-create/select_menu',
          name: 'SelectMenu',
          component: lazyLoading('components/customer/survey/SelectMenu'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.MENU_SELECTION,
          },
          props: true
        },
        {
          path: '/project-create/select_task',
          name: 'SelectTask',
          component: lazyLoading('components/customer/survey/SelectTask'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.TASK_SELECTION,
          },
          props: true,
        },
        {
          path: '/project-create/select_image',
          name: 'SelectImage',
          component: lazyLoading('components/customer/survey/SelectImage'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.IMAGE_SELECTION,
          },
          props: true
        },
        {
          path: '/project-create/select_category',
          name: 'SelectCategory',
          component: lazyLoading('components/customer/survey/SelectCategory'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.SELECT_CATEGORY,
          },
          props: true
        },
        {
          path: '/project-create/select_product_copy',
          name: 'SelectProductCopy',
          component: lazyLoading('components/customer/survey/SelectProductCopy'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.SELECT_PRODUCT_COPY,
          },
          props: true
        },
        {
          path: '/project-create/select_score',
          name: 'SelectScore',
          component: lazyLoading('components/customer/survey/SelectScore'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: '変数選択「ブランドスコア」',
          },
          props: true
        },
        {
          path: '/project-create/confirm',
          name: 'SurveyConfirm',
          component: lazyLoading('components/customer/survey/SurveyConfirm'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.CONTENT_CONFIRMATION,
          },
          props: true
        },
        {
          path: '/project-create/input_concept',
          name: 'InputConcept',
          component: lazyLoading('components/customer/survey/InputConcept'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.INPUT_CONCEPT,
          },
          props: true,
        },
        {
          path: '/project-create/results',
          name: 'Results',
          component: lazyLoading('components/customer/survey/Results'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.VIEW_RESULTS,
          },
          props: true
        },
        {
          path: '/project-create/analysis-results',
          name: 'AnalysisResults',
          component: lazyLoading('components/customer/survey/AnalysisResults'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, valuationSurvey],
            title: constants.TITLE_LANG.ANALYSIS_RESULTS,
          },
          props: true,
        },
        {
          path: '/home',
          name: 'Home',
          component: lazyLoading('components/customer/Home'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE)],
            title: constants.TITLE_LANG.SERVICE_SELECTION,
          },
        },
        {
          path: '/evaluate-shelve',
          name: 'EvaluateShelve',
          component: lazyLoading('components/customer/heatmap_shelve/SelectTypeProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE), valuationShelve],
            title: constants.TITLE_LANG.REGISTER,
          },
        },
        {
          path: '/evaluate-shelve/old-project',
          name: 'EvaluateShelveSelectOldProject',
          component: lazyLoading('components/customer/heatmap_shelve/SelectOldProject'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE), valuationShelve],
            title: constants.TITLE_LANG.PREVIOUS_PROJECTS,
          },
        },
        {
          path: '/evaluate-shelve/index',
          name: 'EvaluateShelveIndex',
          component: lazyLoading('components/customer/heatmap_shelve/HeatmapIndex'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE), valuationShelve],
            title: constants.TITLE_LANG.NEW_PROJECT_REGISTRATION,
          },
          props: true,
        },
        {
          path: '/evaluate-shelve/select-segment',
          name: 'EvaluateShelveSegment',
          component: lazyLoading('components/customer/heatmap_shelve/SelectSegment'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE), valuationShelve],
            title: constants.TITLE_LANG.SEGMENT_SETTING,
          },
          props: true,
        },
        {
          path: '/evaluate-shelve/select-image',
          name: 'EvaluateShelveSelectImage',
          component: lazyLoading('components/customer/heatmap_shelve/SelectImage'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE), valuationShelve],
            title: constants.TITLE_LANG.IMAGE_SELECTION,
          },
          props: true,
        },
        {
          path: '/evaluate-shelve/confirm',
          name: 'EvaluateShelveConfirm',
          component: lazyLoading('components/customer/heatmap_shelve/HeatmapConfirm'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE), valuationShelve],
            title: constants.TITLE_LANG.CONTENT_CONFIRMATION,
          },
          props: true,
        },
        {
          path: '/evaluate-shelve/result',
          name: 'EvaluateShelveResult',
          component: lazyLoading('components/customer/heatmap_shelve/Results'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, plan, accessLanguage(constants.PRODUCT_SERVICE_LANGUAGE), valuationShelve],
            title: constants.TITLE_LANG.VIEW_RESULTS,
          },
          props: true,
        },
        {
          path: '/chat-gpt',
          name: 'ChatIndex',
          component: lazyLoading('components/customer/chat_gpt/ChatIndex'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, accessLanguage(constants.PRODUCT_CHATGPT), planOption, region],
            title: constants.TITLE_LANG.NEW_PROJECT,
          },
          props: true,
        },
        {
          path: '/chat-gpt/menu',
          name: 'ChatSelectMenu',
          component: lazyLoading('components/customer/chat_gpt/SelectMenu'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, accessLanguage(constants.PRODUCT_CHATGPT), planOption, region],
            title: constants.TITLE_LANG.MENU_SELECTION,
          },
          props: true,
        },
        {
          path: '/chat-gpt/information',
          name: 'InputMessage',
          component: lazyLoading('components/customer/chat_gpt/InputMessage'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, accessLanguage(constants.PRODUCT_CHATGPT), planOption, region],
          },
          props: true,
        },
        {
          path: '/creative-package',
          name: 'StableDiffusionIndex',
          component: lazyLoading('components/customer/stable_diffusion/Index'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, planUserGenerateMiddleware, accessLanguage(constants.PRODUCT_STABLE_DIFFUSION), region],
            title: constants.TITLE_LANG.NEW_PROJECT,
          },
          props: true,
        },
        {
          path: '/creative-package/create',
          name: 'StableDiffusionCreate',
          component: lazyLoading('components/customer/stable_diffusion/Create'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, planUserGenerateMiddleware, accessLanguage(constants.PRODUCT_STABLE_DIFFUSION), region],
            title: constants.TITLE_LANG.GENERATE_PACKAGE_DESIGN,
          },
          props: true,
        },
        {
          path: '/creative-package/auto-generate-30',
          name: 'AutoGenerate30ProductInfo',
          component: lazyLoading('components/customer/stable_diffusion/AutoGenerate30ProductInfo'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, planUserGenerateMiddleware, accessLanguage(constants.PRODUCT_STABLE_DIFFUSION), region],
            title: constants.TITLE_LANG.GENERATE_PACKAGE_DESIGN,
          },
          props: true,
        },
        {
          path: '/creative-package/auto-generate-30/results',
          name: 'AutoGenerate30Results',
          component: lazyLoading('components/customer/stable_diffusion/AutoGenerate30Results'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, planUserGenerateMiddleware, accessLanguage(constants.PRODUCT_STABLE_DIFFUSION), region],
            title: constants.TITLE_LANG.GENERATE_PACKAGE_DESIGN,
          },
          props: (route) => ({
            projectId: parseInt(route.query.projectId, 10) || 0,
          }),
        },
        {
          path: '/generate-concept',
          name: 'GenerateConcept',
          component: lazyLoading('components/customer/generate_concept/GenerateConceptIndex'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, generateConcept, generateFreePlanCreated],
            title: constants.TITLE_LANG.NEW_PROJECT,
          },
          props: true,
        },
         {
          path: '/generate-concept/menu',
          name: 'GenerateConceptMenu',
          component: lazyLoading('components/customer/generate_concept/GenerateConceptMenu'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, generateConcept],
            title: constants.TITLE_LANG.MENU_SELECTION,
          },
          props: true,
        },
        {
          path: '/generate-concept/message',
          name: 'GenerateConceptMessage',
          component: lazyLoading('components/customer/generate_concept/GenerateConceptMessage'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin, role.user), validity, generateConcept],
            title: constants.TITLE_LANG.GENERATE_CONCEPT_MESSAGE,
          },
          props: true,
        },
      ],
    },
    {
      path: '/mypage',
      component: BackendLayout,
      children: [
        {
          path: '',
          name: 'MypageIndex',
          component: lazyLoading('components/backend/MyPageIndex'),
          meta: {
            middleware: [parseJwt, auth, roles(role.admin)],
            title: constants.TITLE_LANG.DASHBOARD,
          },
        },
        {
          path: 'plug',
          name: 'Plug',
          component: lazyLoading('components/backend/Plug'),
          meta: {
            middleware: [parseJwt, auth, roles(role.superAdmin)],
            title: constants.TITLE_LANG.DASHBOARD_PLUG,
          },
        },
        {
          path: 'users',
          name: 'Users',
          component: lazyLoading('components/backend/Users'),
          meta: {
            middleware: [parseJwt, auth, roles(role.user)],
            title: constants.TITLE_LANG.DASHBOARD,
          },
        }
      ],
    },
    {
      path: '/login',
      name: 'Login',
      component: lazyLoading('components/Login'),
      meta: {
        middleware: [parseJwt, guest],
        title: constants.TITLE_LANG.LOGIN,
      },
    },
    {
      path: '*',
      name: '404',
      component: lazyLoading('components/404'),
    },
  ],
});

router.beforeEach((to, from, next) => {
  saveRouteAfter(from, to);
  addServiceFacebook(to);
  removeRecaptcha(to);

  if (to.meta.middleware) {
    const middleware = Array.isArray(to.meta.middleware)
      ? to.meta.middleware
      : [to.meta.middleware];

    const context = {
      from,
      next,
      router,
      to,
    };
    const nextMiddleware = nextFactory(context, middleware, 1);
    store.state.lastRoute = from !== undefined ? from.name : null
    console.log('to', to.name)
    console.log('params', store.state.paramsLastRoute)
    console.log('currentMenu', store.state.currentMenu)
    store.state.isResultPage = (to.name === 'Results')
    console.log('last route:', store.state.lastRoute)
    return middleware[0]({ ...context, next: nextMiddleware });
  }

  return next();
});

router.afterEach((to) => {
  Vue.nextTick(() => {
    if (store.getters.region === constants.REGION_CN && to.name === 'HomePage') {
      document.title = to.meta.title;
    } else {
      const title = to.meta.title && to.meta.title[store.getters.getCurrentLanguage] ? to.meta.title[store.getters.getCurrentLanguage] : '';
      document.title = `${title}　|　${constants.DEFAULT_TITLE_LANG[store.getters.getCurrentLanguage]}`;
    }
  });
});

Api.interceptors.response.use(null, error => {
  if (error.response && error.response.status === 401) {
    store.dispatch('logout');

    if (router.currentRoute.matched.some(record => record.meta.middleware && record.meta.middleware.indexOf(auth) !== -1)) {
      router.push({name: 'Login'});
    }
  }

  return Promise.reject(error);
});

export default router;
